import { useAuthenticator } from '@aws-amplify/ui-react';
import { useEffect, useRef, useState } from 'react';
import { Button, Form, Overlay, Tooltip } from 'react-bootstrap';
import Select, { StylesConfig } from 'react-select';
import { apiPrefix } from '../../config';
import { getAuthHeader } from '../../helpers/auth';
import { handleRequestError } from '../../helpers/requests';
import { CognitoUser } from '../../types';
import CompanyAutocomplete from './CompanyAutocomplete';

const regionSelectStyles: StylesConfig<
  { value: string; label: string },
  false
> = {
  control: (base) => ({
    ...base,
    minHeight: 32,
    fontSize: '0.9rem',
  }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  clearIndicator: (base) => ({ ...base, padding: 4 }),
  valueContainer: (base) => ({ ...base, padding: '0 6px' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const toOption = (v: string | null | undefined) =>
  v ? { value: v, label: v } : null;

const UserRow = ({
  user,
  developers,
  regions,
  onChange,
}: {
  user: CognitoUser;
  developers: string[];
  regions: string[];
  onChange: ({ user }: { user: CognitoUser }) => void;
}) => {
  const [toggleLoading, setToggleLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { user: currentUser } = useAuthenticator((context) => [context.user]);
  const buttonRef = useRef(null);

  const [company, setCompany] = useState(user['custom:Company'] ?? '');
  const [division, setDivision] = useState(user['custom:Division'] ?? '');
  const [activeRegion, setActiveRegion] = useState(
    user['custom:ActiveRegion'] ?? ''
  );

  useEffect(() => {
    setCompany(user['custom:Company'] ?? '');
    setDivision(user['custom:Division'] ?? '');
    setActiveRegion(user['custom:ActiveRegion'] ?? '');
  }, [user]);

  const isDirty =
    company !== (user['custom:Company'] ?? '') ||
    division !== (user['custom:Division'] ?? '') ||
    activeRegion !== (user['custom:ActiveRegion'] ?? '');

  const toggleEnabled = async () => {
    setToggleLoading(true);
    const response = await fetch(`${apiPrefix}/cognitouser`, {
      method: 'PATCH',
      headers: await getAuthHeader({ isJson: true }),
      body: JSON.stringify({
        cognitoid: user.Username,
        enable: !user.Enabled,
      }),
    });
    if (response.ok) {
      setInfo('User updated successfully');
      setTimeout(() => setInfo(null), 1000);
      onChange({ user: { ...user, Enabled: !user.Enabled } });
    } else {
      setError('Failed to update user');
      handleRequestError(response);
      setTimeout(() => setError(null), 2000);
    }
    setToggleLoading(false);
  };

  const handleUpdate = async () => {
    setUpdating(true);
    const body: Record<string, string> = { cognitoid: user.Username };
    if (company !== (user['custom:Company'] ?? ''))
      body['custom:Company'] = company;
    if (division !== (user['custom:Division'] ?? ''))
      body['custom:Division'] = division;
    if (activeRegion !== (user['custom:ActiveRegion'] ?? ''))
      body['custom:ActiveRegion'] = activeRegion;

    const response = await fetch(`${apiPrefix}/cognitouser`, {
      method: 'PATCH',
      headers: await getAuthHeader({ isJson: true }),
      body: JSON.stringify(body),
    });
    if (response.ok) {
      setInfo('User updated successfully');
      setTimeout(() => setInfo(null), 1500);
      onChange({
        user: {
          ...user,
          'custom:Company': company,
          'custom:Division': division,
          'custom:ActiveRegion': activeRegion,
        },
      });
    } else {
      setError('Failed to update user');
      handleRequestError(response);
      setTimeout(() => setError(null), 2500);
    }
    setUpdating(false);
  };

  const toggleVariant = user.Enabled ? 'danger' : 'success';
  const toggleText = user.Enabled ? 'Disable' : 'Enable';
  const toggleDisabledText = `${toggleText.slice(0, -1)}ing...`;

  return (
    <tr>
      <td className="fw-medium">{user.email || 'Not Available'}</td>
      <td style={{ minWidth: 180 }}>
        <CompanyAutocomplete
          value={company}
          options={developers}
          onChange={setCompany}
        />
      </td>
      <td style={{ minWidth: 160 }}>
        <Form.Control
          size="sm"
          type="text"
          value={division}
          onChange={(e) => setDivision(e.target.value)}
        />
      </td>
      <td style={{ minWidth: 180 }}>
        <Select
          isClearable
          placeholder="—"
          value={toOption(activeRegion)}
          onChange={(opt) => setActiveRegion(opt?.value ?? '')}
          options={regions.map((r) => ({ value: r, label: r }))}
          styles={regionSelectStyles}
          menuPortalTarget={document.body}
        />
      </td>
      <td>
        {currentUser?.username !== user.Username && (
          <>
            <Button
              ref={buttonRef}
              variant={toggleVariant}
              onClick={toggleEnabled}
              disabled={toggleLoading}
              size="sm"
            >
              {toggleLoading ? toggleDisabledText : toggleText}
            </Button>
            <Overlay
              target={buttonRef.current}
              show={info != null}
              placement="right"
            >
              {(props) => <Tooltip {...props}>{info}</Tooltip>}
            </Overlay>
            <Overlay
              target={buttonRef.current}
              show={error != null}
              placement="right"
            >
              {(props) => <Tooltip {...props}>{error}</Tooltip>}
            </Overlay>
          </>
        )}
      </td>
      <td>
        <Button
          variant="primary"
          size="sm"
          onClick={handleUpdate}
          disabled={!isDirty || updating}
        >
          {updating ? 'Updating...' : 'Update'}
        </Button>
      </td>
    </tr>
  );
};

export const UserRowHead = () => {
  return (
    <tr>
      <th>Email</th>
      <th>Company</th>
      <th>Division</th>
      <th>Active Region</th>
      <th>Enable / Disable</th>
      <th>Update</th>
    </tr>
  );
};

export default UserRow;
